import { useEffect, useRef, useState } from 'react'
import OneSDK from '@frankieone/one-sdk'

import { Spinner } from '@/common/ui/spinner'
import { GOOGLE_PLACES_API_KEY } from '@/kyc/frankieOne/constants'
import {
    getConsentScreenConfig,
    getPersonalScreenConfig,
    getDocumentScreenConfig,
    getResultFailScreenConfig,
    type FrankieOneFlowContext,
} from '../config/frankieOneFormConfig'
import OnboardingKycConsentSubmittedAnalytics from '@/common/analytics/events/OnboardingKycConsentSubmittedAnalytics'
import OnboardingKycPersonalSubmittedAnalytics from '@/common/analytics/events/OnboardingKycPersonalSubmittedAnalytics'
import OnboardingKycDocumentSubmittedAnalytics from '@/common/analytics/events/OnboardingKycDocumentSubmittedAnalytics'
import OnboardingKycReviewSubmittedAnalytics from '@/common/analytics/events/OnboardingKycReviewSubmittedAnalytics'
import OnboardingKycResultSuccessAnalytics from '@/common/analytics/events/OnboardingKycResultSuccessAnalytics'
import OnboardingKycResultFailedAnalytics from '@/common/analytics/events/OnboardingKycResultFailedAnalytics'
import OnboardingKycResultPendingAnalytics from '@/common/analytics/events/OnboardingKycResultPendingAnalytics'

// Progress WITHIN KYC step only (5 screens = 5/13 total steps = 38% of onboarding)
const SCREEN_TO_PROGRESS: Record<string, number> = {
    CONSENT: 0, // 0% of KYC = 58% overall
    PERSONAL: 20, // 20% of KYC = 66% overall (↑8%)
    DOCUMENT: 40, // 40% of KYC = 73% overall (↑7%)
    REVIEW: 60, // 60% of KYC = 81% overall (↑8%)
    RESULT: 80, // 80% of KYC = 88% overall (↑7%)
}

export type PageProps = {
    token?: string
    isPending?: boolean
    isError?: boolean
    error?: Error | null
    isSuccess?: boolean
    isKYCStart?: boolean
    flowContext: FrankieOneFlowContext
}

interface ViewModelProps extends PageProps {
    /** Callback when KYC flow completes — *required for production use*; may be omitted in Storybook previews.
     * Back navigation handled by OneSDK natively. */
    onComplete?: () => void
    /** Callback when sub-progress changes (0-100%) within KYC step */
    onProgressChange?: (subProgress: number) => void
}

export function FrankieOneKycComponent({
    token,
    isPending = false,
    isError = false,
    error,
    isSuccess = false,
    isKYCStart = false,
    flowContext,
    onComplete,
    onProgressChange,
}: ViewModelProps) {
    const mountRef = useRef<HTMLDivElement | null>(null)
    const [sdkError, setSdkError] = useState<string | null>(null)
    const [currentScreen, setCurrentScreen] = useState<'CONSENT' | 'PERSONAL' | 'DOCUMENT' | 'REVIEW' | 'RESULT'>(
        'CONSENT',
    )
    const [isScreenMounted, setIsScreenMounted] = useState(false)

    // Notify parent of sub-progress changes
    useEffect(() => {
        const subProgress = SCREEN_TO_PROGRESS[currentScreen] || 0
        if (onProgressChange) {
            onProgressChange(subProgress)
        }
    }, [currentScreen, onProgressChange])

    useEffect(() => {
        if (!isKYCStart || !isSuccess || !token || !mountRef.current) return

        // Clear any previous SDK errors when starting new initialization
        setSdkError(null)

        type FormComponent = ReturnType<Awaited<ReturnType<typeof OneSDK>>['component']>
        const components: FormComponent[] = []
        let isDisposed = false
        let completionHandled = false

        const initialise = async () => {
            try {
                const sdk = await OneSDK({
                    session: { token },
                    recipe: {
                        form: {
                            provider: {
                                name: 'react',
                                ...(GOOGLE_PLACES_API_KEY
                                    ? {
                                          googleApiKey: GOOGLE_PLACES_API_KEY,
                                          useGoogleAutoCompleteSuggestion: !!GOOGLE_PLACES_API_KEY,
                                      }
                                    : {}),
                            },
                        },
                    },
                })

                if (isDisposed || !mountRef.current) return

                const container = mountRef.current
                const consent = sdk.component('form', getConsentScreenConfig(flowContext))
                const personal = sdk.component('form', getPersonalScreenConfig(flowContext))
                const document = sdk.component('form', getDocumentScreenConfig(flowContext))
                const review = sdk.component('form', { name: 'REVIEW', type: 'manual', verify: true })
                const retry = sdk.component('form', { name: 'RETRY', type: 'manual' })
                const resultFail = sdk.component('form', getResultFailScreenConfig(flowContext))
                components.push(consent, personal, document, review, retry, resultFail)

                // Navigation chain — SDK handles its own back navigation
                consent.on('form:consent:ready', () => {
                    if (isDisposed) return
                    setCurrentScreen('CONSENT')
                    OnboardingKycConsentSubmittedAnalytics.track()
                    personal.mount(container)
                })
                consent.on('form:consent:loaded', () => {
                    if (isDisposed) return
                    setIsScreenMounted(true)
                })
                personal.on('form:personal:ready', () => {
                    if (isDisposed) return
                    setCurrentScreen('PERSONAL')
                    OnboardingKycPersonalSubmittedAnalytics.track()
                    document.mount(container)
                })
                personal.on('form:personal:back', () => {
                    if (isDisposed) return
                    setCurrentScreen('PERSONAL')
                    consent.mount(container)
                })
                document.on('form:document:ready', () => {
                    if (isDisposed) return
                    setCurrentScreen('DOCUMENT')
                    OnboardingKycDocumentSubmittedAnalytics.track()
                    review.mount(container)
                })
                document.on('form:document:back', () => {
                    if (isDisposed) return
                    setCurrentScreen('DOCUMENT')
                    personal.mount(container)
                })
                review.on('form:review:ready', () => {
                    if (isDisposed) return
                    setCurrentScreen('REVIEW')
                    OnboardingKycReviewSubmittedAnalytics.track()
                })

                // Result handling — review (verify: true) auto-shows result screens
                // Navigate to bank account when user presses the CTA on a result screen
                review.on('form:result:success', () => {
                    if (isDisposed || completionHandled) return
                    completionHandled = true
                    setCurrentScreen('RESULT')
                    OnboardingKycResultSuccessAnalytics.track()
                    if (onComplete) onComplete()
                })
                review.on('form:result:failed', () => {
                    if (isDisposed || completionHandled) return
                    completionHandled = true
                    setCurrentScreen('RESULT')
                    OnboardingKycResultFailedAnalytics.track()
                    if (onComplete) onComplete()
                })
                review.on('form:result:pending', () => {
                    if (isDisposed || completionHandled) return
                    completionHandled = true
                    setCurrentScreen('RESULT')
                    OnboardingKycResultPendingAnalytics.track()
                    if (onComplete) onComplete()
                })

                // Smart retry: SDK auto-adapts fields based on failure type (name vs document)
                let retryCount = 0
                review.on('form:result:partial', () => {
                    if (!isDisposed && retryCount < 2) {
                        retryCount++
                        retry.mount(container)
                    } else if (!isDisposed) {
                        resultFail.mount(container)
                    }
                })
                retry.on('form:retry:ready', () => {
                    if (isDisposed) return
                    review.mount(container)
                })
                resultFail.on('form:result:failed', () => {
                    if (isDisposed || completionHandled) return
                    completionHandled = true
                    setCurrentScreen('RESULT')
                    OnboardingKycResultFailedAnalytics.track()
                    if (onComplete) onComplete()
                })

                // Start at first screen
                await consent.mount(container)
            } catch (sdkInitError) {
                if (!isDisposed) {
                    const message = sdkInitError instanceof Error ? sdkInitError.message : 'Failed to start KYC'
                    setSdkError(message)
                }
            }
        }

        initialise()

        return () => {
            isDisposed = true
            components.forEach((c: any) => c?.unmount())
        }
    }, [flowContext, isKYCStart, isSuccess, token, onComplete])

    return (
        <div className="w-full">
            {(isPending || (isSuccess && isKYCStart && !isScreenMounted)) && (
                <div className="flex items-center justify-center py-12">
                    <Spinner size="lg" />
                </div>
            )}

            {(isError || sdkError) && (
                <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </div>
                        <h2 className="mb-2 text-xl font-bold text-gray-900">Error</h2>
                        <p className="text-gray-700">{error?.message || sdkError || 'Failed to start KYC'}</p>
                    </div>
                </div>
            )}

            {isSuccess && (
                <div
                    ref={mountRef}
                    className="w-full h-[calc(var(--vh,1vh)*100-80px)] sm:h-[600px] sm:mb-8 md:mb-0 overflow-y-auto"
                />
            )}
        </div>
    )
}
